import { useState, useEffect, useCallback } from 'react';
import { Box, TextField, IconButton, Button, Typography } from '@mui/material';
import {
  AddCircleOutline as AddIcon,
  RemoveCircleOutline as RemoveIcon,
} from '@mui/icons-material';

interface KVPair {
  id: string;
  key: string;
  value: string;
  wasOriginallyString?: boolean;
}

interface KeyValueEditorProps {
  value: Record<string, unknown>;
  onChange: (data: Record<string, unknown>) => void;
  label?: string;
}

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
}

function recordToKVPairs(data: Record<string, unknown>): KVPair[] {
  return Object.entries(data).map(([key, val]) => ({
    id: generateId(),
    key,
    value: typeof val === 'string' ? val : JSON.stringify(val),
    wasOriginallyString: typeof val === 'string',
  }));
}

function kvPairsToRecord(pairs: KVPair[]): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const pair of pairs) {
    if (pair.key) {
      // If the original value was a string, preserve it as a string
      if (pair.wasOriginallyString) {
        result[pair.key] = pair.value;
      } else {
        // Otherwise try to parse it (for objects, arrays, booleans, etc.)
        try {
          result[pair.key] = JSON.parse(pair.value);
        } catch {
          result[pair.key] = pair.value;
        }
      }
    }
  }
  return result;
}

/**
 * Key/value pair editor for JSON data fields.
 * Converts a Record into editable key-value rows.
 */
const KeyValueEditor = ({ value, onChange, label = 'Data' }: KeyValueEditorProps) => {
  const [pairs, setPairs] = useState<KVPair[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && value) {
      setPairs(recordToKVPairs(value));
      setInitialized(true);
    }
  }, [value, initialized]);

  const updatePairs = useCallback((newPairs: KVPair[]) => {
    setPairs(newPairs);
    onChange(kvPairsToRecord(newPairs));
  }, [onChange]);

  const handleKeyChange = useCallback((id: string, newKey: string) => {
    updatePairs(pairs.map(p => p.id === id ? { ...p, key: newKey } : p));
  }, [pairs, updatePairs]);

  const handleValueChange = useCallback((id: string, newValue: string) => {
    updatePairs(pairs.map(p => p.id === id ? { ...p, value: newValue } : p));
  }, [pairs, updatePairs]);

  const handleAdd = useCallback(() => {
    updatePairs([...pairs, { id: generateId(), key: '', value: '', wasOriginallyString: true }]);
  }, [pairs, updatePairs]);

  const handleRemove = useCallback((id: string) => {
    updatePairs(pairs.filter(p => p.id !== id));
  }, [pairs, updatePairs]);

  return (
    <Box sx={{ mb: 2 }}>
      {label && (
        <Typography variant="subtitle2" gutterBottom>
          {label}
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
        Add key-value pairs. String values are preserved as-is. Use JSON syntax for objects/arrays.
      </Typography>
      {pairs.map(pair => (
        <Box
          key={pair.id}
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
        >
          <TextField
            size="small"
            placeholder="Key"
            value={pair.key}
            onChange={e => handleKeyChange(pair.id, e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            placeholder="Value"
            value={pair.value}
            onChange={e => handleValueChange(pair.id, e.target.value)}
            sx={{ flex: 2 }}
          />
          <IconButton
            size="small"
            onClick={() => handleRemove(pair.id)}
            color="error"
            title="Remove pair"
          >
            <RemoveIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={handleAdd}
      >
        Add Pair
      </Button>
    </Box>
  );
};

export default KeyValueEditor;
